import React, { useState } from 'react';
import { useHomepageSettings } from '../../hooks/useHomepageSettings';
import ContactFormModal from '../ContactFormModal';

const CTASection: React.FC = () => {
  const { data: settings, isLoading } = useHomepageSettings();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const defaultHeading = 'Ready to Protect Your Investment?';
  const defaultSubheading =
    'Join hundreds of homeowners who trust OnArrival Global with their vacation properties. Get started today with a free consultation.';
  const defaultBackgroundImage =
    'https://images.unsplash.com/photo-1505142468610-359e7d316be0?q=80&w=2070';
  const defaultButtonText = 'Schedule Consultation';
  const defaultTrustIndicators = [
    { icon: 'star', text: 'Fully Insured & Bonded' },
    { icon: 'check', text: 'Member NHWA' },
    { icon: 'clock', text: '24/7 Emergency Response' },
  ];

  const heading = settings?.ctaSection?.heading || defaultHeading;
  const subheading = settings?.ctaSection?.subheading || defaultSubheading;
  const backgroundImage =
    settings?.ctaSection?.backgroundImage?.asset?.url || defaultBackgroundImage;
  const buttonText = settings?.ctaSection?.buttonText || defaultButtonText;
  // Note: buttonLink from CMS is no longer used - button now opens contact modal
  const trustIndicators =
    settings?.ctaSection?.trustIndicators || defaultTrustIndicators;
  const isEnabled = settings?.ctaSection?.enabled !== false;

  // Don't render if disabled
  if (!isEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <section className="relative py-24 md:py-32 overflow-hidden bg-primary-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </section>
    );
  }

  // Map icon names to SVG elements
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'star':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      case 'check':
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'clock':
        return (
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-700/90 to-blue-900/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Heading */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {heading}
          </h2>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed">
            {subheading}
          </p>

          {/* CTA Button */}
          <button
            onClick={() => setIsContactModalOpen(true)}
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-primary-500 font-bold text-lg rounded-full hover:bg-primary-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105 transform cursor-pointer"
          >
            {buttonText}
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </button>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/80">
            {trustIndicators.map((indicator, index) => (
              <div key={index} className="flex items-center gap-2">
                {getIcon(indicator.icon)}
                <span>{indicator.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Wave */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-[0] rotate-180">
        <svg
          className="relative block w-full h-12"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="fill-sand-50"
          />
        </svg>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </section>
  );
};

export default CTASection;
